import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Features, ModuleNames } from '../../../common/model/account-definition';
import { UserAccountService } from '../../../common/service/user-account.service';
import { getMonthName } from '../../../core/helper/month';
import { ColumnVisibility } from '../../../core/model/column-definition';
import { FilterDefinitionItem } from '../../../core/model/filter-definition-item';
import { MenuItemDefinition } from '../../../core/model/menu-item-definition';
import { CommandDispatcher } from '../../../core/service/command/command-dispatcher.service';
import { ColumnDefinitionFactory } from '../../../core/service/factory/column-definition-factory.service';
import { CommandFactoryService } from '../../../core/service/factory/command-factory.service';
import { MessageService } from '../../../core/service/message.service';
import { NavigationService } from '../../../core/service/navigation.service';
import { rentalObjectCommand } from '../../command/rental-object.command';
import { CustomPropertyVisibilityType, FormOfLease, FormOfLeaseToString } from '../../model/const/enums';
import { Dwelling } from '../../model/dwelling';
import { DwellingCommandHandlerResolver } from '../../service/command/dwelling-command-handler-resolver.service';
import { DwellingRentalObjectCommandFactory } from '../../service/command/factory/dwelling-rental-object-command-factory.service';
import { BuildingService } from '../../service/data/building.service';
import { CompanyService } from '../../service/data/company.service';
import { CustomPropertyService } from '../../service/data/custom-property.service';
import { EntranceService } from '../../service/data/entrance.service';
import { PropertyService } from '../../service/data/property.service';
import { DwellingService } from './../../service/data/dwelling.service';
import { PagedObjectManagerComponent } from '../../../core/component/paged-object-manager/paged-object-manager';
import { DwellingExportCommandHandler } from '../../service/command/dwelling-export-command-handler.service';
import { CustomPropertyHelper } from '../../helper/customproperties-helper';
import { LettingFeatureFilterHelper } from '../../helper/letting-feature-filter-helper';
import { LettingFeatureColumnHelper } from '../../helper/letting-feature-column-helper';
import { LettingFeatureService } from '../../service/data/letting-feature.service';
import { forkJoin } from 'rxjs';
import { standardCommand } from '../../../core/model/command';

@Component({
    selector: 'app-dwelling-rental-object-manager',
    templateUrl: 'dwelling-rental-object-manager.component.html',
    styleUrls: [
        'dwelling-rental-object-manager.component.scss',
        './../../../core/component/multiselect-object-manager/multiselect-object-manager.component.scss',
    ],
    providers: [
        DwellingCommandHandlerResolver,
        DwellingExportCommandHandler,
        { provide: CommandFactoryService, useClass: DwellingRentalObjectCommandFactory },
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DwellingRentalObjectManagerComponent extends PagedObjectManagerComponent<Dwelling> {
  constructor(
    private readonly _cdf: ColumnDefinitionFactory,
    private readonly _dataDwellingService: DwellingService,
    dispatcher: CommandDispatcher,
    messageService: MessageService,
    navigationService: NavigationService,
    handlerResolver: DwellingCommandHandlerResolver,
    private readonly _companyService: CompanyService,
    private readonly _buildingService: BuildingService,
    private readonly _propertyService: PropertyService,
    private readonly _customPropertyService: CustomPropertyService,
    private readonly _entranceService: EntranceService,
    private readonly _accountService: UserAccountService,
    private readonly _ch: CustomPropertyHelper,
    private readonly _lettingFeatureService: LettingFeatureService,
    private readonly _lettingFeatureFilterHelper: LettingFeatureFilterHelper,
    private readonly _lettingFeatureColumnHelper: LettingFeatureColumnHelper
  ) {
    super(_dataDwellingService, messageService, navigationService, dispatcher, handlerResolver);

    this.columnDefinition = [
      _cdf.create('RENTAL_OBJECT_LIST.RENTAL_ID_COLUMN', 'rentalId'),
      _cdf.create('RENTAL_OBJECT_LIST.STREET_COLUMN', 'address'),
      _cdf.create('RENTAL_OBJECT_LIST.RENTAL_OBJECT_TYPE_COLUMN', 'rentalObjectType'),
      _cdf.create('COMMON.APARTMENTNUMBER', 'apartmentNumber', ColumnVisibility.optional),
      _cdf.createNumber('RENTAL_OBJECT_FORM.SURFACE_BOA_LONG', 'surface', { visibility: ColumnVisibility.optional, totalSum: true }),
      _cdf.create('COMMON.COMPANY_NUMBER', 'companyNumber', ColumnVisibility.optional),
      _cdf.create('COMMON.COMPANY', 'companyName', ColumnVisibility.optional),
      _cdf.create('PROPERTY.NUMBER', 'propertyNumber', ColumnVisibility.optional),
      _cdf.create('COMMON.PROPERTY', 'propertyName', ColumnVisibility.optional),
      _cdf.create('BUILDING_FORM.NUMBER', 'buildingNumber', ColumnVisibility.optional),
      _cdf.create('BUILDING_FORM.EDIT_HEADER', 'buildingName', ColumnVisibility.optional),
      _cdf.create('ENTRANCE.ENTRANCE', 'entranceName', ColumnVisibility.optional),
      _cdf.create('DWELLING_FORM.FLOOR_LABEL', 'floor'),
      _cdf.createBoolean('DWELLING_FORM.ELEVATOR_LABEL', 'elevator', ColumnVisibility.optional),
      _cdf.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE', 'tenantOneName', { visibility: ColumnVisibility.optional, allowSort: this }),
      _cdf.createPhoneNumber('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE_PHONE', 'tenantOnePhone', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.create('LEASE_AGREEMENT.CONTRACT_HOLDER_ONE_EMAIL', 'tenantOneEmail', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createDate('COMMON.AGREEMENT_START_DATE', 'leaseAgreementFromDate', { visibility: ColumnVisibility.optional, allowSort: this }),
      _cdf.createDate('COMMON.AGREEMENT_END_DATE', 'leaseAgreementLastBillingDate', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createDate('RENTAL_OBJECT.BLOCKED_FROM', 'blockedFrom', ColumnVisibility.optional),
      _cdf.createDate('RENTAL_OBJECT.BLOCKED_TO', 'blockedTo', ColumnVisibility.optional),
      _cdf.create('RENTAL_OBJECT.BLOCKING_REASON', 'blockingReason', ColumnVisibility.optional),
      _cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN', 'monthlyRent', { allowSort: this }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_COLUMN_LEASE_AGREEMENT', 'currentMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_CURRENT_COLUMN', 'rentLossCurrentMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN', 'monthlyRentNext', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.MOUNTHLY_RENT_NEXT_COLUMN_LEASE_AGREEMENT', 'nextMonthRentLeaseAgreement', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.RENT_LOSS_MONTH_NEXT_COLUMN', 'rentLossNextMonth', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('COMMON.YEAR_RENT', 'yearRent', { visibility: ColumnVisibility.optional, allowSort: this }),
      _cdf.createMoney('COMMON.YEAR_RENT_PER_SQM', 'yearRentPerSqm', { visibility: ColumnVisibility.optional, allowSort: this }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.NEGOTIATION_YEAR_RENT', 'negotiationYearRent', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.NEGOTIATION_YEAR_RENT_PER_SQM', 'negotiationYearRentPerSqm', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('COMMON.NORMRENT', 'normRentRH91', { visibility: ColumnVisibility.optional, allowSort: this }),
      _cdf.createMoney('RENTAL_OBJECT_LIST.BVK91RENT', 'rentBVK91', { visibility: ColumnVisibility.optional, allowSort: this }),
      _cdf.createMoney('COMMON.RENT_LOSS_THIS_YEAR', 'rentLossCurrentYear', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.createMoney('COMMON.RENT_LOSS_PREVIOUS_YEAR', 'rentLossPreviousYear', {
        visibility: ColumnVisibility.optional,
        allowSort: this,
      }),
      _cdf.create('DWELLING_LEASE_AGREEMENT_FORM.ATTIC_STORAGE_NUMBER_LABEL', 'atticStorageNumber', ColumnVisibility.optional),
      _cdf.create('DWELLING_LEASE_AGREEMENT_FORM.BASEMENT_STORAGE_NUMBER_LABEL', 'basementStorageNumber', ColumnVisibility.optional),
      _cdf.create('DWELLING_LEASE_AGREEMENT_FORM.OTHER_STORAGE_NUMBER_LABEL', 'otherStorageNumber', ColumnVisibility.optional),
      _cdf.create('DWELLING_LEASE_AGREEMENT_FORM.PARKING_INCLUDED_IN_RENT_LABEL', 'parkingIncludedInRent', ColumnVisibility.optional),
      _cdf.createBoolean('DWELLING.FURNISHED', 'furnished', ColumnVisibility.optional),
      _cdf.createTranslation('RENTAL_OBJECT.FORM_OF_LEASE', 'formOfLeaseText', ColumnVisibility.optional),
    ];

    this.menuDefinition = [new MenuItemDefinition('LEASE_AGREEMENT.COMMAND.EXPORT', rentalObjectCommand.export)];
    this.addMenuItemDefinition('LEASE_AGREEMENT.COMMAND.RENTAL_CHANGE', rentalObjectCommand.feeChange);
    this.addMenuItemDefinition('RENTAL_OBJECT.COMMAND.COPY_ROOMS', rentalObjectCommand.copyRooms);
    this.addMenuItemDefinition('RENTAL_OBJECT.COMMAND.SET_LETTING_FEATURES', rentalObjectCommand.setLettingFeatures);

    this.AddSubscription(
      this._accountService.current.subscribe({
        next: account => {
          if (account.hasAccess(ModuleNames.Properties, Features.EditDwelling)) {
            this.allowNew = true;
          } else {
            this.allowNew = false;
          }
          if (account.hasAccess(ModuleNames.Properties, Features.ChangeRentalObjectCategory)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT_CATEGORY.COMMAND', rentalObjectCommand.changeCategory);
          }

          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_INVOICES', rentalObjectCommand.showInvoices);
          if (
            account.hasAccess(ModuleNames.Properties, Features.LinkPropertyBaseDocuments) ||
            account.hasAccess(ModuleNames.Properties, Features.EditDwelling)
          ) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.LINK_DOCUMENT', rentalObjectCommand.linkDocument);
          }
          this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.SHOW_DOCUMENTS', rentalObjectCommand.showDocuments);
          this.addContextMenuItemDefinition('REPORT.STAIR_CASE_LIST_WORD', rentalObjectCommand.showStairCaseList);

          const profileSettings = this._accountService.profileSettings;
          if (profileSettings.useBrf) {
            this.addContextMenuItemDefinition('LEASE_AGREEMENT.COMMAND.BROKER_INFORMATION', rentalObjectCommand.printBrokerInformation);
            this.columnDefinition.push(
              this._cdf.createDecimal('COOPERATIVE_HOUSING.SHARE', 'condominiumShare', {
                visibility: ColumnVisibility.optional,
                totalSum: true,
              })
            );
            this.columnDefinition.push(
              this._cdf.create('COOPERATIVE_HOUSING.ECONOMICAL_PLAN_NUMBER', 'economicalPlanNumber', ColumnVisibility.optional)
            );
          }

          const letting_homeq = account.hasAccess(ModuleNames.Letting, Features.Letting_HomeQ);
          if (letting_homeq) {
            this.addMenuItemDefinition('RENTAL_OBJECT.COMMAND.SEND_TO_HOMEQ', rentalObjectCommand.sendToHomeQ);
          }

          if (letting_homeq || account.hasAccess(ModuleNames.Letting, Features.Letting_DhMarket)) {
            this.addContextMenuItemDefinition('RENTAL_OBJECT.COMMAND.ADVERTISE', rentalObjectCommand.advertise);
          }
          this.addContextMenuItemDefinition('EDIT_BTN', standardCommand.edit);
          if (this.allowNew) {
            this.addContextMenuItemDefinition('DELETE_BTN', standardCommand.delete);
          }
        },
      })
    );

    this.title = 'RENTAL_OBJECT_LIST.DWELLING_HEADER';

    this.preferencesKey = 'rent.dwelling-rental-object-manager.list';
  }

  override filterInternal(filterState: any): void {
    this._ch.applyCustomPropertyFilters(filterState);
    this._lettingFeatureFilterHelper.applyLettingFeatureFilters(filterState);
  }
  ngOnInit() {
    this.AddSubscription(
      forkJoin([this._customPropertyService.getList(), this._dataDwellingService.getFilterDefinition(), this._lettingFeatureService.getAvailableForRentalObjects()]).subscribe(
        ([customProperties, definition, lettingFeatures]) => {
          this.defaultFilters = definition.defaultFields;
          const rentalObjectTypes = definition.rentalObjectTypes.map(x => {
            return { caption: x.name, value: x.id };
          });
          rentalObjectTypes.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

          const yearSelection: { caption: string; value: number }[] = [];
          const currentYear = new Date().getFullYear();
          for (let offset = -2; offset <= 4; offset++)
            yearSelection.push({ caption: (currentYear + offset).toString(), value: currentYear + offset });

          const monthSelection: { caption: string; value: number }[] = [];
          for (let monthNumber = 1; monthNumber <= 12; monthNumber++)
            monthSelection.push({ caption: getMonthName(monthNumber), value: monthNumber });

          const articles = definition.articles.map(article => {
            return { caption: article.toString(), value: article.id };
          });
          articles.splice(0, 0, { caption: 'ALL_TEXT', value: '' });

          var formOfLeaseSelection = [
            { caption: 'ALL_TEXT', value: null },
            { caption: FormOfLeaseToString(FormOfLease.Lease), value: FormOfLease.Lease },
            { caption: FormOfLeaseToString(FormOfLease.CooperativeHousing), value: FormOfLease.CooperativeHousing },
            { caption: FormOfLeaseToString(FormOfLease.Ownership), value: FormOfLease.Ownership },
          ];

          const selectedCompanies = definition.companyIds
            ? Array.isArray(definition.companyIds)
              ? definition.companyIds
              : [definition.companyIds]
            : [];

          const selectedProperties = definition.propertyIds
            ? Array.isArray(definition.propertyIds)
              ? definition.propertyIds
              : [definition.propertyIds]
            : [];

          const selectedBuildings = definition.buildingIds
            ? Array.isArray(definition.buildingIds)
              ? definition.buildingIds
              : [definition.buildingIds]
            : [];

          const selectedEntrances = definition.entranceIds
            ? Array.isArray(definition.entranceIds)
              ? definition.entranceIds
              : [definition.entranceIds]
            : [];

          this.filterDefinition = [

            new FilterDefinitionItem(
              'COMPANY.LIST_HEADER',
              'companyIds',
              selectedCompanies,
              this._companyService,
              definition.selectedFields.includes('companyIds') || definition.selectedFields.includes('company'),
              true
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.PROPERTY_LABEL',
              'propertyIds',
              selectedProperties,
              this._propertyService,
              definition.selectedFields.includes('propertyIds') || definition.selectedFields.includes('property'),
              true
            ),
            new FilterDefinitionItem(
              'BUILDING_FORM.EDIT_HEADER',
              'buildingIds',
              selectedBuildings,
              this._buildingService,
              definition.selectedFields.includes('buildingIds') || definition.selectedFields.includes('building'),
              true
            ),
            new FilterDefinitionItem(
              'ENTRANCE.ENTRANCE',
              'entranceIds',
              selectedEntrances,
              this._entranceService,
              definition.selectedFields.includes('entranceIds') || definition.selectedFields.includes('entrance'),
              true
            ),
            new FilterDefinitionItem(
              'COMMON.RENTAL_OBJECT_TYPE',
              'rentalObjectType',
              definition.rentalObjectType,
              rentalObjectTypes,
              definition.selectedFields.includes('rentalObjectType')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_FORM.RENTAL_OBJECT_NUMBER_LABEL',
              'rentalId',
              definition.rentalId,
              null,
              definition.selectedFields.includes('rentalId')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT_LIST.STREET_COLUMN',
              'street',
              definition.street,
              null,
              definition.selectedFields.includes('street')
            ),
            new FilterDefinitionItem(
              'COMMON.DISPLAY_YEAR_RENT_YEAR',
              'rentFromYear',
              definition.rentFromYear,
              yearSelection,
              definition.selectedFields.includes('rentFromYear')
            ),
            new FilterDefinitionItem(
              'COMMON.DISPLAY_YEAR_RENT_MONTH',
              'rentFromMonth',
              definition.rentFromMonth,
              monthSelection,
              definition.selectedFields.includes('rentFromMonth')
            ),
            new FilterDefinitionItem(
              'DWELLING_FORM.ELEVATOR_LABEL',
              'elevator',
              definition.elevator,
              [
                { caption: 'ALL_TEXT', value: null },
                { caption: 'YES_TEXT', value: true },
                { caption: 'NO_TEXT', value: false },
              ],
              definition.selectedFields.includes('elevator')
            ),
            new FilterDefinitionItem(
              'ARTICLE.EDIT_HEADER',
              'article',
              definition.article,
              articles,
              definition.selectedFields.includes('article')
            ),
            new FilterDefinitionItem(
              'RENTAL_OBJECT.FORM_OF_LEASE',
              'formOfLease',
              definition.formOfLease,
              formOfLeaseSelection,
              definition.selectedFields.includes('formOfLease')
            ),
          ];
          const customPropertiesFilters = this._ch.generateCustomPropertyFilters(
            customProperties,
            definition,
            CustomPropertyVisibilityType.Company |
              CustomPropertyVisibilityType.Dwelling |
              CustomPropertyVisibilityType.Property |
              CustomPropertyVisibilityType.Building
          );
          this._ch.createCustomPropertyColumnDefinition(
            customProperties,
            this.columnDefinition,
            CustomPropertyVisibilityType.Company |
              CustomPropertyVisibilityType.Dwelling |
              CustomPropertyVisibilityType.Property |
              CustomPropertyVisibilityType.Building
          );
          this._lettingFeatureColumnHelper.createLettingFeatureColumnDefinitions(lettingFeatures, this.columnDefinition);
          const lettingFeatureFilters = this._lettingFeatureFilterHelper.generateLettingFeatureFilters(lettingFeatures, definition);
          this.filterDefinition = [...this.filterDefinition, ...customPropertiesFilters, ...lettingFeatureFilters];
        }
      )
    );
  }
}
